/** The shared sound library: public/game-data/audio/library.json plus the files next to it. */

export interface LibrarySound {
  id: string;
  name: string;
  /** relative to the audio folder */
  file: string;
  category: string;
  /** events this sound was picked for, best fit first */
  tags: string[];
  duration: number;
  /** made for this game (Unity Assets/Sounds) */
  original?: boolean;
  credit: string;
}

export interface LibraryCredit {
  title: string;
  author: string;
  authorUrl: string | null;
  url: string | null;
  license: string;
  licenseUrl: string | null;
  source: string;
  note: string;
}

export interface SoundLibrary {
  base: string;
  sounds: LibrarySound[];
  byId: Map<string, LibrarySound>;
  /** event id -> suggested sound ids, best first */
  suggestions: Record<string, string[]>;
  credits: Record<string, LibraryCredit>;
}

const cache = new Map<string, Promise<SoundLibrary>>();

export function loadLibrary(gameData = '/game-data'): Promise<SoundLibrary> {
  const base = `${gameData}/audio`;
  let p = cache.get(base);
  if (!p) {
    p = fetch(`${base}/library.json`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`library.json: ${r.status}`))))
      .then((raw: Omit<SoundLibrary, 'base' | 'byId'>) => ({ ...raw, base, byId: new Map(raw.sounds.map((s) => [s.id, s])) }));
    p.catch(() => cache.delete(base));
    cache.set(base, p);
  }
  return p;
}

export function soundUrl(lib: SoundLibrary, sound: LibrarySound): string {
  return `${lib.base}/${sound.file}`;
}

export function creditLine(c: LibraryCredit): string {
  if (c.license === 'Original') return 'Original Cactus Fiesta sound';
  return `"${c.title}" by ${c.author} (${c.source}), ${c.license}`;
}
